import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import './AlertToast.css';

// Listens for `tradermap:trade-alert` CustomEvents dispatched whenever a
// sound notification plays. Surfaces a stack of transient cards in the
// top-right corner so users know *which* coin and side triggered the
// sound. Auto-dismisses after 4 seconds per card; cards collapse when
// more than 3 stack up.

const TTL_MS = 4200;
const MAX_VISIBLE = 3;

function formatDollar(v) {
  const n = Number(v) || 0;
  if (n >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(2)}M`;
  if (n >= 1e3) return `$${(n / 1e3).toFixed(0)}K`;
  return `$${Math.round(n)}`;
}

export default function AlertToast() {
  const { t } = useTranslation();
  const [alerts, setAlerts] = useState([]);
  const idCounterRef = useRef(0);

  const pushAlert = useCallback((detail) => {
    if (!detail || !detail.coin) return;
    const id = ++idCounterRef.current;
    setAlerts((prev) => {
      const next = [...prev, { ...detail, id }];
      return next.slice(-MAX_VISIBLE);
    });
    setTimeout(() => {
      setAlerts((prev) => prev.filter((a) => a.id !== id));
    }, TTL_MS);
  }, []);

  useEffect(() => {
    const handler = (e) => pushAlert(e.detail);
    window.addEventListener('tradermap:trade-alert', handler);
    return () => window.removeEventListener('tradermap:trade-alert', handler);
  }, [pushAlert]);

  if (alerts.length === 0) return null;

  return (
    <div className="alert-toast-stack" role="status" aria-live="polite">
      {alerts.map((a) => {
        const isLiq = a.kind === 'liquidation';
        const isLongSide = a.side === 'buy';
        // For trades: buy = LONG taker, sell = SHORT taker.
        // For liquidations: sell-side = long forced out (so LONG liq).
        const sideTone = isLiq
          ? (a.side === 'sell' ? 'liq-long' : 'liq-short')
          : (isLongSide ? 'long' : 'short');
        const sideLabel = isLiq
          ? (a.side === 'sell' ? t('chartPage.side.longLiq', 'LONG LIQ') : t('chartPage.side.shortLiq', 'SHORT LIQ'))
          : (isLongSide ? t('chartPage.side.long', 'LONG') : t('chartPage.side.short', 'SHORT'));
        const kindGlyph = isLiq ? '☲' : '◆';
        return (
          <div
            key={a.id}
            className={`alert-toast ${sideTone}`}
            data-kind={a.kind}
          >
            <span className="alert-toast-glyph" aria-hidden="true">{kindGlyph}</span>
            <span className="alert-toast-coin">{a.coin}</span>
            <span className={`alert-toast-side ${sideTone}`}>{sideLabel}</span>
            <span className="alert-toast-size">{formatDollar(a.dollarSize)}</span>
            {a.exchange && <span className="alert-toast-exch">{a.exchange}</span>}
          </div>
        );
      })}
    </div>
  );
}
